import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, ShieldCheck, Tag, Target, Rocket } from 'lucide-react';
import WizardLayout from './wizard/WizardLayout';
import WelcomeStep1MeetDippy from './welcome/WelcomeStep1MeetDippy';
import WelcomeStep2Autonomy from './welcome/WelcomeStep2Autonomy';
import WelcomeStep3Brand from './welcome/WelcomeStep3Brand';
import WelcomeStep4Mission from './welcome/WelcomeStep4Mission';
import WelcomeStep5Launch from './welcome/WelcomeStep5Launch';
import { useWelcomeState } from './welcome/useWelcomeState';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import api from '@/lib/api';

const STEPS = [
  { label: 'Meet Dippy', icon: Bot },
  { label: 'Autonomy', icon: ShieldCheck },
  { label: 'Brand', icon: Tag },
  { label: 'Mission', icon: Target },
  { label: 'Launch', icon: Rocket },
];

const STEP_INFO = [
  { title: 'Meet Dippy', description: "Your AI dropship partner is about to go to work." },
  { title: 'How hands-on do you want to be?', description: 'Pick how much autonomy Dippy gets.' },
  { title: 'What do you sell?', description: 'Dippy uses this to filter suppliers and stay on-brand.' },
  { title: "Dippy's first mission", description: 'Pick what Dippy should tackle first.' },
  { title: 'Launch Dippy', description: 'Review your choices and kick off the first mission.' },
];

export default function Welcome() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const addToast = useUIStore((s) => s.addToast);
  const state = useWelcomeState();
  const { updateState, resetState } = state;

  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  // Fetch initial brand/product/store name from the backend. Prefills
  // step 3. Also detects if onboarding is already complete and bounces
  // the user away.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        const res = await api.get<{
          onboarding_completed: boolean;
          store_name: string;
          brand_description: string;
          product_type: string;
        }>('/admin/agent/welcome/bootstrap');
        if (cancelled) return;
        if (res.onboarding_completed) {
          navigate('/admin/agent', { replace: true });
          return;
        }
        // Pre-fill brand/product only if the user hasn't started editing
        if (!state.brandDescription) updateState({ brandDescription: res.brand_description });
        if (!state.productType) updateState({ productType: res.product_type });
        setBootstrapped(true);
      } catch (err) {
        // If the bootstrap endpoint fails, let the user proceed with
        // blank fields — they can still fill them in manually.
        if (!cancelled) setBootstrapped(true);
      }
    }
    bootstrap();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [state.currentStep]);

  if (!isInitialized || !bootstrapped) {
    return <div className="p-8 text-center text-text-secondary">Loading…</div>;
  }
  if (!user) {
    return <div className="p-8 text-center text-text-secondary">Sign in to continue.</div>;
  }

  function isCurrentStepValid(): boolean {
    switch (state.currentStep) {
      case 0:
        return true;
      case 1:
        return !!state.approvalPreset;
      case 2:
        return state.brandDescription.trim().length >= 20 && state.categories.length > 0;
      case 3:
        if (!state.mission) return false;
        if (state.mission === 'research' && state.researchBrands.length === 0) return false;
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  }

  function handleNext() {
    if (state.currentStep >= STEPS.length - 1) return;
    const completed = state.completedSteps.includes(state.currentStep)
      ? state.completedSteps
      : [...state.completedSteps, state.currentStep];
    updateState({ currentStep: state.currentStep + 1, completedSteps: completed });
  }

  function handleBack() {
    if (state.currentStep === 0) return;
    updateState({ currentStep: state.currentStep - 1 });
  }

  function handleStepClick(step: number) {
    if (step > state.currentStep && !isCurrentStepValid()) return;
    updateState({ currentStep: step });
  }

  async function handleLaunch() {
    setLaunchError(null);
    setIsLaunching(true);
    try {
      // Skip flow: the admin never filled in brand/categories/autonomy —
      // send only the mission so the backend can take its own defaults.
      // The real missions still require the full payload.
      const payload: Record<string, unknown> = state.mission === 'skip'
        ? { mission: 'skip' }
        : {
            approval_preset: state.approvalPreset,
            brand_description: state.brandDescription,
            product_type: state.productType,
            categories: state.categories,
            exclude_keywords: state.excludeKeywords,
            mission: state.mission,
          };
      if (state.mission === 'research') payload.research_brands = state.researchBrands;

      const res = await api.post<{
        task_id: string | null;
        onboarding_completed: boolean;
        redirect: string;
      }>('/admin/agent/welcome/launch', payload);

      resetState();
      addToast(
        state.mission === 'skip'
          ? 'Dippy is standing by. Fire missions anytime from the AI Agent dashboard.'
          : 'Dippy is on the job. First report in about 15 minutes.',
        'success'
      );
      navigate(res.redirect || '/admin/agent', { replace: true });
    } catch (err: any) {
      // 409 means onboarding was already completed (two-tab race, refresh
      // after finish, etc.). The API client throws a plain Error with the
      // server's error message as its `.message`, so match on that.
      if (/already complete/i.test(err?.message || '')) {
        resetState();
        navigate('/admin/agent', { replace: true });
        return;
      }
      setLaunchError(err?.message || 'Failed to launch Dippy. Please try again.');
    } finally {
      setIsLaunching(false);
    }
  }

  async function handleSkip() {
    updateState({ mission: 'skip', currentStep: 4 });
  }

  const stepInfo = STEP_INFO[state.currentStep];

  return (
    <WizardLayout
      currentStep={state.currentStep}
      totalSteps={STEPS.length}
      stepTitle={stepInfo.title}
      stepDescription={stepInfo.description}
      onNext={state.currentStep === STEPS.length - 1 ? handleLaunch : handleNext}
      onBack={handleBack}
      onStepClick={handleStepClick}
      completedSteps={state.completedSteps}
      steps={STEPS}
      isNextDisabled={!isCurrentStepValid() || isLaunching}
      isNextLoading={isLaunching}
      nextLabel={state.currentStep === STEPS.length - 1 ? (state.mission === 'skip' ? 'Save & Skip' : 'Start Dippy') : 'Continue'}
      hideNext={state.currentStep === STEPS.length - 1}
      savedAt={state.savedAt || null}
    >
      {state.currentStep === 0 && <WelcomeStep1MeetDippy onSkip={handleSkip} />}
      {state.currentStep === 1 && (
        <WelcomeStep2Autonomy
          value={state.approvalPreset}
          onChange={(preset) => updateState({ approvalPreset: preset })}
        />
      )}
      {state.currentStep === 2 && (
        <WelcomeStep3Brand
          brandDescription={state.brandDescription}
          productType={state.productType}
          categories={state.categories}
          excludeKeywords={state.excludeKeywords}
          onChange={(partial) => updateState(partial)}
        />
      )}
      {state.currentStep === 3 && (
        <WelcomeStep4Mission
          mission={state.mission}
          researchBrands={state.researchBrands}
          onChange={(partial) => updateState(partial)}
        />
      )}
      {state.currentStep === 4 && (
        <WelcomeStep5Launch
          approvalPreset={state.approvalPreset}
          brandDescription={state.brandDescription}
          productType={state.productType}
          categories={state.categories}
          mission={state.mission}
          researchBrands={state.researchBrands}
          isLaunching={isLaunching}
          launchError={launchError}
          onLaunch={handleLaunch}
        />
      )}
    </WizardLayout>
  );
}
